import React, { useState, useEffect, useCallback } from "react";
import { Container, Alert, Modal, Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import SettingsPageHeader from "../components/SettingsPageHeader";
import { useAuth } from "../hooks/useAuth";
import { useRolePermissions } from "../hooks/useRolePermissions";
import {
  nurseryGroupAPI,
  nurseryLocationAPI,
  nurseryAPI,
  clearCache,
  createNurseryGroup,
  createNurseryLocation,
} from "../utils/api";
import type {
  NurseryGroup,
  NurseryLocation,
  InheritableSettings,
} from "../types/nurseryGroup";
import type {
  NurseryGroupCreationData,
  NurseryLocationCreationData,
} from "../utils/api";
import type { Nursery } from "../types/auth";
import HierarchyTree from "./HierarchyTree";
import WizardModal from "./WizardModal";
import { useWizardState } from "./HierarchyManagementHooks";
import {
  sanitizeInheritedValue,
  getTimezoneValue,
  isDirectOverride,
} from "./HierarchyManagementUtils";
import { SETTINGS_KEYS, DEFAULT_SETTINGS_VALUES } from "./HierarchyManagementConstants";

type WizardData = Partial<
  NurseryGroupCreationData &
    NurseryLocationCreationData & {
      nursery_id: string;
      nursery_name: string;
      status: 1 | 2;
      email?: string;
      phone?: string;
      description?: string;
      nursery_group_id?: number;
      location_name?: string;
      location_description?: string;
      location_timezone?: string;
      location_address?: string;
      location_repo_th?: number;
      location_similarity_addition?: number;
      location_valid_th_from?: number;
      location_valid_th_to?: number;
      nursery_timezone?: string;
      nursery_address?: string;
      nursery_repo_th?: number;
      nursery_similarity_addition?: number;
      nursery_valid_th_from?: number;
      nursery_valid_th_to?: number;
    } & InheritableSettings
>;

export type { WizardData };

const HierarchyManagement: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'hierarchyManagement' });
  const { currentNursery, userRolePermissionsLoading } = useAuth();
  const { isSuperAdmin } = useRolePermissions();

  const [groups, setGroups] = useState<NurseryGroup[]>([]);
  const [locations, setLocations] = useState<NurseryLocation[]>([]);
  const [allNurseries, setAllNurseries] = useState<Nursery[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalType, setModalType] = useState<
    "group" | "location" | "nursery" | null
  >(null);
  const [modalAction, setModalAction] = useState<
    "create" | "edit" | "assign-nursery"
  >("create");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);

  const {
    wizardStep,
    setWizardStep,
    wizardData,
    setWizardData,
    nurseryMode,
    setNurseryMode,
    override,
    setOverride,
    initialOverride,
    setInitialOverride,
    nurseryOverride,
    setNurseryOverride,
    initialNurseryOverride,
    setInitialNurseryOverride,
    resetWizard,
  } = useWizardState();

  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const closeConfirmDialog = () => {
    setConfirmLoading(false);
    setConfirmDialog(null);
  };

  const openConfirmDialog = (
    title: string,
    message: string,
    onConfirm: () => Promise<void>,
  ) => {
    setConfirmDialog({ show: true, title, message, onConfirm });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalError(null);
    setModalSuccess(null);
    setActionLoading(false);
    resetWizard();
    setEditingId(null);
  };

  const fetchData = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);

        const groupsRes = await nurseryGroupAPI.listGroups();
        setGroups(groupsRes.data);

        const locationsPromises = groupsRes.data.map((group) =>
          nurseryLocationAPI.listLocations(group.id),
        );
        const locationsRes = await Promise.all(locationsPromises);
        setLocations(locationsRes.flatMap((res) => res.data));

        const nurseriesRes = await nurseryAPI.getNurseries();
        setAllNurseries(nurseriesRes.data);
      } catch {
        setError(t('fetchFailed'));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    clearCache();
    fetchData();
  }, [fetchData]);

  const getLocationsForGroup = (groupId: number) =>
    locations.filter((l) => l.nursery_group_id === groupId);

  const getNurseriesForLocation = (locationId: number) =>
    allNurseries.filter((c) => c.nursery_location_id === locationId);

  const handleOpenCreateModal = (
    type: "group" | "location",
    parentId?: number,
  ) => {
    setModalType(type);
    setModalAction("create");
    setEditingId(null);
    setModalError(null);
    setModalSuccess(null);
    setWizardStep(1);

    const initialData: WizardData = {
      ...DEFAULT_SETTINGS_VALUES,
    };

    if (type === "location" && parentId) {
      initialData.nursery_group_id = parentId;
      const group = groups.find((g) => g.id === parentId);
      if (group) {
        initialData.timezone = group.timezone ?? getTimezoneValue(group.timezone);
        initialData.address = group.address ?? "";
        initialData.repo_th = group.repo_th ?? DEFAULT_SETTINGS_VALUES.repo_th;
        initialData.similarity_addition = group.similarity_addition ?? DEFAULT_SETTINGS_VALUES.similarity_addition;
        initialData.valid_th_from = group.valid_th_from ?? DEFAULT_SETTINGS_VALUES.valid_th_from;
        initialData.valid_th_to = group.valid_th_to ?? DEFAULT_SETTINGS_VALUES.valid_th_to;
      }
    }
    setWizardData(initialData);
    setNurseryMode("attach");
    if (type === "location") {
      const initialOver = SETTINGS_KEYS.reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as Record<string, boolean>);
      setOverride(initialOver);
      setInitialOverride(initialOver);
    }
    setShowModal(true);
  };

  const handleOpenEditModal = (type: "group" | "location", item: any) => {
    setModalType(type);
    setModalAction("edit");
    setEditingId(item.id);
    setModalError(null);
    setModalSuccess(null);
    setWizardStep(1);
    setNurseryMode("attach");

    if (type === "group") {
      setWizardData({
        name: item.name,
        description: item.description || "",
        timezone: sanitizeInheritedValue(item.timezone) || "",
        address: sanitizeInheritedValue(item.address) || "",
        repo_th: sanitizeInheritedValue(item.repo_th) ?? 0,
        similarity_addition: sanitizeInheritedValue(item.similarity_addition) ?? 0,
        valid_th_from: sanitizeInheritedValue(item.valid_th_from) ?? 0,
        valid_th_to: sanitizeInheritedValue(item.valid_th_to) ?? 0,
      });
    } else {
      const group = groups.find((g) => g.id === item.nursery_group_id);
      const groupValues = {
        timezone: sanitizeInheritedValue(group?.timezone),
        address: sanitizeInheritedValue(group?.address),
        repo_th: sanitizeInheritedValue(group?.repo_th),
        similarity_addition: sanitizeInheritedValue(group?.similarity_addition),
        valid_th_from: sanitizeInheritedValue(group?.valid_th_from),
        valid_th_to: sanitizeInheritedValue(group?.valid_th_to),
      };
      const locValues = {
        timezone: sanitizeInheritedValue(item.timezone),
        address: sanitizeInheritedValue(item.address),
        repo_th: sanitizeInheritedValue(item.repo_th),
        similarity_addition: sanitizeInheritedValue(item.similarity_addition),
        valid_th_from: sanitizeInheritedValue(item.valid_th_from),
        valid_th_to: sanitizeInheritedValue(item.valid_th_to),
      };
      setWizardData({
        nursery_group_id: item.nursery_group_id,
        timezone: groupValues.timezone || undefined,
        address: groupValues.address || undefined,
        repo_th: groupValues.repo_th ?? undefined,
        similarity_addition: groupValues.similarity_addition ?? undefined,
        valid_th_from: groupValues.valid_th_from ?? undefined,
        valid_th_to: groupValues.valid_th_to ?? undefined,

        location_name: item.name,
        location_description: item.description || "",
        location_timezone: locValues.timezone || "",
        location_address: locValues.address || "",
        location_repo_th: locValues.repo_th ?? undefined,
        location_similarity_addition: locValues.similarity_addition ?? undefined,
        location_valid_th_from: locValues.valid_th_from ?? undefined,
        location_valid_th_to: locValues.valid_th_to ?? undefined,
      });
      const currentOverrides = SETTINGS_KEYS.reduce((acc, key) => {
        acc[key] = isDirectOverride(locValues[key], groupValues[key], key);
        return acc;
      }, {} as Record<string, boolean>);
      setOverride(currentOverrides);
      setInitialOverride(currentOverrides);
    }
    setShowModal(true);
  };

  const handleOpenEditNurseryModal = (comp: Nursery) => {
    setModalType("nursery");
    setModalAction("edit");
    setEditingId(comp.id);
    setModalError(null);
    setModalSuccess(null);
    setWizardStep(1);
    setNurseryMode("attach");

    const loc = locations.find((l) => l.id === comp.nursery_location_id);
    const group = groups.find((g) => g.id === loc?.nursery_group_id);

    const groupValues = {
      timezone: sanitizeInheritedValue(group?.timezone),
      address: sanitizeInheritedValue(group?.address),
      repo_th: sanitizeInheritedValue(group?.repo_th),
      similarity_addition: sanitizeInheritedValue(group?.similarity_addition),
      valid_th_from: sanitizeInheritedValue(group?.valid_th_from),
      valid_th_to: sanitizeInheritedValue(group?.valid_th_to),
    };
    const locationValues = {
      timezone: sanitizeInheritedValue(loc?.timezone),
      address: sanitizeInheritedValue(loc?.address),
      repo_th: sanitizeInheritedValue(loc?.repo_th),
      similarity_addition: sanitizeInheritedValue(loc?.similarity_addition),
      valid_th_from: sanitizeInheritedValue(loc?.valid_th_from),
      valid_th_to: sanitizeInheritedValue(loc?.valid_th_to),
    };
    const nurseryValues = {
      timezone: sanitizeInheritedValue(comp.timezone),
      address: sanitizeInheritedValue(comp.address),
      repo_th: sanitizeInheritedValue(comp.repo_th),
      similarity_addition: sanitizeInheritedValue(comp.similarity_addition),
      valid_th_from: sanitizeInheritedValue(comp.valid_th_from),
      valid_th_to: sanitizeInheritedValue(comp.valid_th_to),
    };

    setWizardData({
      nursery_id: comp.nursery_id,
      nursery_name: comp.name,
      status: comp.status as 1 | 2,
      email: comp.email || "",
      phone: comp.phone || "",
      description: comp.description || "",
      location_timezone: locationValues.timezone || "",
      location_address: locationValues.address || "",
      location_repo_th: locationValues.repo_th ?? undefined,
      location_similarity_addition: locationValues.similarity_addition ?? undefined,
      location_valid_th_from: locationValues.valid_th_from ?? undefined,
      location_valid_th_to: locationValues.valid_th_to ?? undefined,

      nursery_timezone: nurseryValues.timezone || "",
      nursery_address: nurseryValues.address || "",
      nursery_repo_th: nurseryValues.repo_th ?? undefined,
      nursery_similarity_addition: nurseryValues.similarity_addition ?? undefined,
      nursery_valid_th_from: nurseryValues.valid_th_from ?? undefined,
      nursery_valid_th_to: nurseryValues.valid_th_to ?? undefined,
    });

    const newOverrides: Record<string, boolean> = {};
    SETTINGS_KEYS.forEach((s) => {
      const compVal = nurseryValues[s];
      const parentVal = locationValues[s] ?? groupValues[s];
      newOverrides[s] = isDirectOverride(compVal, parentVal, s);
    });
    setNurseryOverride(newOverrides);
    setInitialNurseryOverride(newOverrides);
    setShowModal(true);
  };

  const handleOpenAssignModal = (loc: NurseryLocation) => {
    setModalType("location");
    setModalAction("assign-nursery");
    setEditingId(loc.id);
    setModalError(null);
    setModalSuccess(null);
    setWizardStep(1);
    setNurseryMode("attach");

    const group = groups.find((g) => g.id === loc.nursery_group_id);
    setWizardData({
      nursery_group_id: loc.nursery_group_id,
      timezone: group?.timezone || undefined,
      address: group?.address || undefined,
      repo_th: group?.repo_th || undefined,
      similarity_addition: group?.similarity_addition || undefined,
      valid_th_from: group?.valid_th_from || undefined,
      valid_th_to: group?.valid_th_to || undefined,

      // Actual values at location level
      location_timezone: loc.timezone || undefined,
      location_address: loc.address || undefined,
      location_repo_th: loc.repo_th ?? undefined,
      location_similarity_addition: loc.similarity_addition ?? undefined,
      location_valid_th_from: loc.valid_th_from ?? undefined,
      location_valid_th_to: loc.valid_th_to ?? undefined,
    });
    setNurseryOverride({});
    setShowModal(true);
  };

  const handleUnassignNursery = async (comp: Nursery) => {
    if (!comp.nursery_location_id) return;

    const nurseriesInLocation = getNurseriesForLocation(
      comp.nursery_location_id,
    );
    if (nurseriesInLocation.length <= 1) {
      setError(t('lastNurseryUnassign'));
      return;
    }

    openConfirmDialog(
      t('confirmUnassignTitle'),
      t('confirmUnassignMessage', { name: comp.name }),
      async () => {
        setConfirmLoading(true);
        try {
          const { id } = comp;
          await nurseryAPI.unassignLocation(id);
          fetchData(true);
          closeConfirmDialog();
        } catch (err: any) {
          setError(err.response?.data?.message || t('unassignFailed'));
          setConfirmLoading(false);
        }
      },
    );
  };

  const handleDelete = async (
    type: "group" | "location",
    id: number,
    parentId?: number,
  ) => {
    if (type === "location") {
      const locationsInGroup = getLocationsForGroup(parentId!);
      if (locationsInGroup.length <= 1) {
        setError(t('lastLocationDelete'));
        return;
      }
    }

    const entityName = type === "group" ? t('entityGroup') : t('entityLocation');
    openConfirmDialog(
      t('deleteEntityTitle', { entity: entityName }),
      t('deleteEntityMessage', { entity: entityName }),
      async () => {
        setConfirmLoading(true);
        try {
          if (type === "group") {
            await nurseryGroupAPI.deleteGroup(id);
          } else {
            await nurseryLocationAPI.deleteLocation(parentId!, id);
          }
          fetchData(true);
          closeConfirmDialog();
        } catch (err: any) {
          setError(err.response?.data?.message || t('deleteFailed'));
          setConfirmLoading(false);
        }
      },
    );
  };

  const handleNext = () => {
    setModalError(null);
    if (modalType === "group") {
      if (wizardStep === 1 && !wizardData.name?.trim()) {
        setModalError(t('groupNameRequired'));
        return;
      }
      if (wizardStep === 2 && !wizardData.location_name?.trim()) {
        setModalError(t('locationNameRequired'));
        return;
      }
    } else if (modalType === "location") {
      if (wizardStep === 1 && !wizardData.location_name?.trim()) {
        setModalError(t('locationNameRequired'));
        return;
      }
    }
    setWizardStep((s) => s + 1);
  };

  const handleBack = () => {
    setModalError(null);
    setWizardStep((s) => s - 1);
  };

  const helperSendNurserySettings = (
    prefix: "nursery" | "location" = "nursery",
  ) => {
    const payload: any = {};
    SETTINGS_KEYS.forEach((s) => {
      const isOverridden =
        prefix === "nursery" ? nurseryOverride[s] : override[s];
      const initialIsOverridden =
        prefix === "nursery" ? initialNurseryOverride[s] : initialOverride[s];
      const valueKey = prefix === "nursery" ? `nursery_${s}` : `location_${s}`;

      if (isOverridden) {
        let val = wizardData[valueKey as keyof WizardData];
        val = sanitizeInheritedValue(val);

        if (s === "timezone") {
          val = getTimezoneValue(val as string);
        }

        if (val !== undefined && val !== null) {
          payload[s] = val;
        } else if (
          [
            "repo_th",
            "similarity_addition",
            "valid_th_from",
            "valid_th_to",
          ].includes(s)
        ) {
          payload[s] = 0;
        } else {
          payload[s] = null;
        }
      } else if (initialIsOverridden) {
        payload[s] = null;
      }
    });
    return payload;
  };

  const handleWizardSubmit = async () => {
    setModalError(null);

    if (
      modalAction === "assign-nursery" ||
      (modalAction === "create" &&
        (modalType === "group" ? wizardStep === 3 : wizardStep === 2))
    ) {
      if (nurseryMode === "attach" && !wizardData.nursery_id?.trim()) {
        setModalError(t('nurseryRequired'));
        return;
      }
      if (nurseryMode === "create") {
        if (!wizardData.nursery_id?.trim()) {
          setModalError(t('nurseryIdRequired'));
          return;
        }
        if (!wizardData.nursery_name?.trim()) {
          setModalError(t('nurseryNameRequired'));
          return;
        }
      }
    }

    setActionLoading(true);
    try {
      const nurserySettings = helperSendNurserySettings("nursery");

      if (modalAction === "assign-nursery") {
        if (nurseryMode === "attach") {
          const comp = allNurseries.find(
            (c) => c.nursery_id === wizardData.nursery_id,
          );
          if (comp) {
            const { id, ...fullData } = comp;
            await nurseryAPI.updateNursery(id, {
              ...nurserySettings,
              email: fullData.email ?? undefined,
              nursery_location_id: editingId,
              name: fullData.name,
              phone: fullData.phone,
              description: fullData.description,
              status: fullData.status,
            });
          }
        } else {
          await nurseryAPI.createNursery({
            nursery_id: wizardData.nursery_id!,
            name: wizardData.nursery_name!,
            status: wizardData.status || 1,
            nursery_location_id: editingId,
            timezone: getTimezoneValue(wizardData.location_timezone),
            email: wizardData.email,
            phone: wizardData.phone,
            description: wizardData.description,
            ...nurserySettings,
          });
        }
      } else if (modalType === "nursery" && modalAction === "edit") {
        const comp = allNurseries.find((c) => c.id === editingId);
        if (comp) {
          const { id, ...fullData } = comp;
          await nurseryAPI.updateNursery(id, {
            ...fullData,
            name: wizardData.nursery_name,
            status: wizardData.status,
            email: wizardData.email,
            phone: wizardData.phone,
            description: wizardData.description,
            ...nurserySettings,
          });
        }
      } else if (modalType === "group") {
        if (modalAction === "create") {
          const locationSettings: any = {};
          SETTINGS_KEYS.forEach((s) => {
            if (override[s]) {
              locationSettings[s] = wizardData[`location_${s}` as keyof WizardData];
            }
          });

          const payload: NurseryGroupCreationData = {
            name: wizardData.name!,
            description: wizardData.description,
            timezone: getTimezoneValue(wizardData.timezone),
            address: wizardData.address,
            repo_th: wizardData.repo_th,
            similarity_addition: wizardData.similarity_addition,
            valid_th_from: wizardData.valid_th_from,
            valid_th_to: wizardData.valid_th_to,
            location: {
              name: wizardData.location_name!,
              description: wizardData.location_description,
              ...locationSettings,
            },
            nursery: {
              mode: nurseryMode,
              nursery_id: wizardData.nursery_id!,
              name:
                nurseryMode === "create" ? wizardData.nursery_name : undefined,
              status:
                nurseryMode === "create" ? wizardData.status || 1 : undefined,
              email: nurseryMode === "create" ? wizardData.email : undefined,
              phone: nurseryMode === "create" ? wizardData.phone : undefined,
              description: nurseryMode === "create" ? wizardData.description : undefined,
              ...nurserySettings,
            },
          };
          await createNurseryGroup(payload);
        } else {
          await nurseryGroupAPI.updateGroup(editingId!, {
            name: wizardData.name,
            description: wizardData.description,
            timezone: getTimezoneValue(wizardData.timezone),
            address: wizardData.address,
            repo_th: wizardData.repo_th,
            similarity_addition: wizardData.similarity_addition,
            valid_th_from: wizardData.valid_th_from,
            valid_th_to: wizardData.valid_th_to,
          });
        }
      } else if (modalType === "location") {
        if (modalAction === "create") {
          const locationSettings: any = {};
          SETTINGS_KEYS.forEach((s) => {
            if (override[s]) {
              if (s === "timezone") {
                locationSettings[s] = getTimezoneValue(wizardData.location_timezone);
              } else {
                locationSettings[s] = wizardData[`location_${s}` as keyof WizardData];
              }
            }
          });

          const payload: NurseryLocationCreationData = {
            name: wizardData.location_name!,
            description: wizardData.location_description,
            ...locationSettings,
            nursery: {
              mode: nurseryMode,
              nursery_id: wizardData.nursery_id!,
              name:
                nurseryMode === "create" ? wizardData.nursery_name : undefined,
              status:
                nurseryMode === "create" ? wizardData.status || 1 : undefined,
              email: nurseryMode === "create" ? wizardData.email : undefined,
              phone: nurseryMode === "create" ? wizardData.phone : undefined,
              description: nurseryMode === "create" ? wizardData.description : undefined,
              ...nurserySettings,
            },
          };
          await createNurseryLocation(
            wizardData.nursery_group_id!,
            payload,
          );
        } else {
          const locationPayload: any = {
            name: wizardData.location_name,
            description: wizardData.location_description,
          };
          SETTINGS_KEYS.forEach((s) => {
            if (override[s]) {
              if (s === "timezone") {
                locationPayload[s] = getTimezoneValue(wizardData.location_timezone);
              } else {
                locationPayload[s] = wizardData[`location_${s}` as keyof WizardData];
              }
            } else if (initialOverride[s]) {
              locationPayload[s] = null;
            }
          });

          await nurseryLocationAPI.updateLocation(
            wizardData.nursery_group_id!,
            editingId!,
            locationPayload,
          );
        }
      }
      setModalSuccess(
        modalAction === "create" ? t('successCreated') : t('successUpdated'),
      );
      fetchData(true);
      setTimeout(handleCloseModal, 2000);
    } catch (err: any) {
      setModalError(err.response?.data?.message || t('submitError'));
    } finally {
      setActionLoading(false);
    }
  };



  if (!isSuperAdmin) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="px-3 py-4">
          <SettingsPageHeader
            title={t('title')}
            settingsPath={
              currentNursery
                ? `/nursery/${currentNursery.nursery_id}/settings`
                : "/settings"
            }
          />
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <Alert variant="warning">
                <h5>{t('accessDeniedTitle')}</h5>
                <p>{t('accessDeniedBody')}</p>
              </Alert>
            </div>
          </div>
        </Container>
      </Layout>
    );
  }

  if (loading || userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container fluid className="px-3 py-4">
          <SettingsPageHeader
            title={t('title')}
            settingsPath={
              currentNursery
                ? `/nursery/${currentNursery.nursery_id}/settings`
                : "/settings"
            }
          />
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="text-center">
              <Spinner animation="border" variant="primary" className="mb-3" />
              <p className="text-muted fw-medium">
                {t('loading')}
              </p>
            </div>
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3 py-4">
        <SettingsPageHeader
          title={t('title')}
          settingsPath={
            currentNursery
              ? `/nursery/${currentNursery.nursery_id}/settings`
              : "/settings"
          }
        />

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <HierarchyTree
          groups={groups}
          locations={locations}
          allNurseries={allNurseries}
          onCreateGroup={() => handleOpenCreateModal("group")}
          onCreateLocation={(groupId) => handleOpenCreateModal("location", groupId)}
          onEditGroup={(group) => handleOpenEditModal("group", group)}
          onEditLocation={(location) => handleOpenEditModal("location", location)}
          onEditNursery={(nursery) => handleOpenEditNurseryModal(nursery)}
          onAssignNursery={(location) => handleOpenAssignModal(location)}
          onDeleteGroup={(groupId) => handleDelete("group", groupId)}
          onDeleteLocation={(locationId, groupId) => handleDelete("location", locationId, groupId)}
          onUnassignNursery={(nursery) => handleUnassignNursery(nursery)}
        />

        <WizardModal
          show={showModal}
          onHide={handleCloseModal}
          modalType={modalType}
          modalAction={modalAction}
          wizardStep={wizardStep}
          wizardData={wizardData}
          setWizardData={setWizardData}
          nurseryMode={nurseryMode}
          setNurseryMode={setNurseryMode}
          override={override}
          setOverride={setOverride}
          nurseryOverride={nurseryOverride}
          setNurseryOverride={setNurseryOverride}
          modalError={modalError}
          modalSuccess={modalSuccess}
          actionLoading={actionLoading}
          onNext={handleNext}
          onBack={handleBack}
          onSubmit={handleWizardSubmit}
          availableNurseries={allNurseries.filter(
            (c) => !getNurseriesForLocation(editingId || 0).some(
              (comp) => comp.nursery_id === c.nursery_id
            )
          )}
        />

        <Modal
          show={!!confirmDialog?.show}
          onHide={closeConfirmDialog}
          centered
          backdrop="static"
          keyboard={!confirmLoading}
        >
          <Modal.Header closeButton={!confirmLoading}>
            <Modal.Title>{confirmDialog?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{confirmDialog?.message}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeConfirmDialog} disabled={confirmLoading}>
              {t('cancel', { ns: 'common', keyPrefix: 'buttons' })}
            </Button>
            <Button
              variant="danger"
              onClick={async () => {
                if (!confirmDialog?.onConfirm) return;
                await confirmDialog.onConfirm();
              }}
              disabled={confirmLoading}
            >
              {confirmLoading ? <Spinner size="sm" /> : t('confirm')}
            </Button>
          </Modal.Footer>
        </Modal>


      </Container>
    </Layout>
  );
};

export default HierarchyManagement;
